import { getUploadSignedUrl } from "@/store/actions"
import FetchFileService from "./FetchFileService"
import { fileConverter } from "./fileConverter"
import { cdn_url, storyPrefix, VideoPrefix } from "./path"
import { safeKeyComponent } from "./common";

type FileProgress = {
  index: number;
  fileName: string;
  percent: number;
  overallPercent: number;
};

export const complementaryUpload = async (
  dispatch: any,
  files: Array<{ file: File | { url: string; name?: string }, isFeatured?: boolean }>,
  story: any,
  isVideo = false,
  isImage = false,
  onProgress?: (p: FileProgress) => void
) => {

  const isAlreadyUploaded: any = (file: any) =>
    !!file?.url ||
    !!file?.name?.includes?.('stories/') ||
    !!file?.name?.includes?.('videos/');

  // Normalize and validate incoming items
  const normalized = files.map((mediaItem, index) => {
    const f: any = mediaItem?.file;

    if (!f || (!f.url && !(f instanceof File))) {
      console.warn(`complementaryUpload: Skipping invalid file[${index}]`, JSON.stringify(f));
      return null;
    }
    if (f instanceof File && (!f.name || f.size === 0)) {
      console.warn(`complementaryUpload: Skipping invalid File object[${index}]`, { name: f.name, size: f.size });
      return null;
    }
    return {
      raw: mediaItem,
      file: f,
      isFeatured: !!mediaItem?.isFeatured,
      already: isAlreadyUploaded(f),
    };
  }).filter(item => item !== null);

  // Partition
  const toUpload = normalized.filter((x: any) => !x.already);
  const skipped = normalized.filter((x: any) => x.already);
  if (toUpload.length === 0) {

    return [];
  }

  // Progress only over upload items, only for videos
  const perFilePct: number[] = new Array(toUpload.length).fill(0);
  const updateOverall = (i: number, fileName: string, pct: number) => {
    perFilePct[i] = pct;
    const sum = perFilePct.reduce((a, b) => a + b, 0);
    const overall = Math.floor(sum / toUpload.length);
    if (isVideo) {
      onProgress?.({ index: i, fileName, percent: pct, overallPercent: overall });
    }
  };

  const uploaded: any[] = [];
  await Promise.all(
    toUpload.map((item: any, i: any) =>
      new Promise<void>(async (resolve, reject) => {
        try {
          const file: any = item.file;
          const isFeatured = item.isFeatured;

          const originalName = file?.name?.split('/')?.pop()?.replace(/\.[^/.]+$/, '') || `file_${i}`;
          const extension = file?.name?.match(/\.[^/.]+$/)?.[0] || '';
          const safeTitle = safeKeyComponent(story?.title || 'untitled');
          const timestamp = Date.now() + i * 1000;

          const fileName = isVideo
            ? `${VideoPrefix}/${safeTitle}/memories/${originalName}${extension}`
            : isImage
              ? `${storyPrefix}/${safeTitle}/memories/${originalName}.webp`
              : `${storyPrefix}/${safeTitle}/memories/${originalName}${extension}`;
          const uploadUrl = await new Promise<string>((urlResolve, urlReject) => {
            dispatch(
              getUploadSignedUrl(
                {
                  file: fileName,
                  type: isVideo ? 'video/mp4' : isImage ? 'image/webp' : (file?.type || 'application/octet-stream'),
                },
                (res: any) => {
                  try {
                    const url = res?.value?.url?.uploadUrl || res?.value?.uploadUrl || res?.value?.url;
                    if (!url) {
                      console.error(`complementaryUpload: Signed upload URL missing for file[${i}]`);
                      urlReject(new Error('Signed upload URL missing'));
                      return;
                    }
                    urlResolve(url);
                  } catch (err: any) {
                    console.error(`complementaryUpload: Error processing getUploadSignedUrl response for file[${i}]`, {
                      error: err.message,
                      stack: err.stack,
                    });
                    urlReject(err);
                  }
                }
              )
            );
          }).catch(err => {
            console.error(`complementaryUpload: getUploadSignedUrl failed for file[${i}]`, { error: err.message, stack: err.stack });
            throw err;
          });

          let body: any | Blob = file;
          let contentType: string =
            isVideo ? 'video/mp4' : isImage ? 'image/webp' : (file?.type || 'application/octet-stream');


          if (isImage) {
            try {
              body = await fileConverter(file);
            } catch (err: any) {
              console.error(`complementaryUpload: fileConverter failed for file[${i}]`, { error: err.message, stack: err.stack });
              throw err;
            }
          }
          const response: any = await FetchFileService(
            uploadUrl,
            'PUT',
            body,
            contentType,
            (pct) => updateOverall(i, fileName, pct)
          );

          if (response?.ok) {
            uploaded.push({ fileName, isFeatured, status: isVideo ? 'pending' : 'done' });
            resolve();
          } else {
            console.error(`complementaryUpload: FetchFileService failed for file[${i}]`, {
              status: response?.status,
              statusText: response?.statusText,
            });
            reject(new Error(`Upload failed for ${fileName}`));
          }
        } catch (err: any) {
          console.error(`complementaryUpload: Error uploading file[${i}]`, { error: err.message, stack: err.stack });
          reject(err);
        }
      })
    )
  ).catch(err => {
    console.error("complementaryUpload: Promise.all error", { error: err.message, stack: err.stack });
    throw err;
  });

  return uploaded;
};

export const complementaryDownload = async (dispatch: any, files: any) => {



  //// console.log("md-file-download", files);

  return new Promise((resolve, reject) => {
    try {
      //// console.log("files got in com download are", files)
      const data: any = [];

      // Ensure files is always an array
      const filesArray = Array.isArray(files) ? files : [files];

      for (const file of filesArray) {

        //// console.log("inside the file array")
        //// console.log("file isnide the array", file)
        const fileName: string | undefined = file?.fileName;
        const filePath =
          file?.versions?.[0]?.video
            ? file?.versions[0]?.video
            : file?.url
              ? file?.url
              : file?.preview
                ? file?.preview
                : fileName;
        //// console.log("file path", filePath)
        if (!filePath) continue; // Skip invalid files

        // Construct the CDN URL
        const fullUrl = `${cdn_url}${filePath}`;

        const featured = typeof file === "object" && file?.isFeatured
          ? file.isFeatured :
          file?.isFeatured


        // Push the URL and use it as a preview
        data.push({ url: fullUrl, preview: fullUrl, isFeatured: featured });
      }
      // //// console.log("data files", data)
      resolve(data);
    } catch (error) {
      console.error("Error processing files:", error);
      reject(error);
    }
  });
};
